import { getConfig } from '../../config';
import { v4 as uuidv4 } from 'uuid';

const MAX_ATTEMPTS = 3;
const TIMEOUT_MS = 15000;

interface requestHeaders {
  'Content-Type': string,
  'Authorization': string,
  'Idempotency-Key': string,
  'X-JustiFi-PaymentsJS-version': string,
  'Account'?: string
}

interface requestBody {
  payment_method: object,
  email?: string
}

async function tokenizePaymentMethod(
  clientId: string,
  paymentMethod: any,
  account?: string | undefined
) {
  const idempotencyKey = `tokenize-${uuidv4()}`;

  for (let attempts = 0; attempts < MAX_ATTEMPTS; attempts++) {
    try {
      const response = await makeTokenizeRequest(clientId, paymentMethod, idempotencyKey, account);
      const shouldRetryHeader = response.headers.get('Should-Retry');
      if (shouldRetryHeader === 'true') continue;
      return await response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') continue;
      throw error;
    }
  }

  return { error: 'Request timed out' };
};

async function makeTokenizeRequest(
  clientId: string,
  paymentMethod: any,
  idempotencyKey: string,
  account?: string | undefined
) {
  const config = getConfig();
  const requestUrl = `${config.apiOrigin}/v1/js/payment_methods`;
  const requestHeaders: requestHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Basic ${window.btoa(clientId)}:`,
    'Idempotency-Key': idempotencyKey,
    'X-JustiFi-PaymentsJS-version': 'legacy'
  };

  if (account) {
    requestHeaders['Account'] = account;
  }

  let email;

  if (paymentMethod.email) {
    email = paymentMethod.email;
    delete paymentMethod.email;
  }

  const requestBody: requestBody = { payment_method: paymentMethod };

  if (email) {
    requestBody.email = email;
  }

  const requestOptions = {
    method: 'POST',
    headers: requestHeaders,
    body: JSON.stringify(requestBody),
  };
  return fetchWithTimeout(requestUrl, requestOptions);
}

async function fetchWithTimeout(requestUrl: string, options: any) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const response = await fetch(requestUrl, {
    ...options,
    signal: controller.signal
  });

  clearTimeout(timer);

  return response;
}

export default tokenizePaymentMethod;
